// src/pages/Pricing.jsx
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { auth } from "../utils/firebaseConfig";
import { useTheme } from "../themes";
import Footer from "../components/Footer";

const Pricing = () => {
  const navigate = useNavigate();
  const { isDarkTheme } = useTheme();

  // 🔁 Toggle Monthly / Annual
  const [isAnnual, setIsAnnual] = useState(false);

  // 🔐 Estado de suscripción del usuario
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);

  // 💰 Precios
  const MONTHLY_PRICE = 4.99;
  const ANNUAL_PRICE = 49.99;

  const currentPrice = isAnnual ? ANNUAL_PRICE : MONTHLY_PRICE;
  const effectiveMonthlyPrice = isAnnual
    ? (ANNUAL_PRICE / 12).toFixed(2)
    : MONTHLY_PRICE.toFixed(2);

  const labelPlan = isAnnual ? "Annual Access" : "Monthly Access";

  // 📥 Traer info de suscripción desde backend
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setActiveSubscription(null);
          setLoadingSub(false);
          return;
        }

        const token = await user.getIdToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setActiveSubscription(data.activeSubscription || null);
        } else {
          setActiveSubscription(null);
        }
      } catch (err) {
        console.error("Error fetching subscription:", err);
        setActiveSubscription(null);
      } finally {
        setLoadingSub(false);
      }
    };

    fetchSubscription();
  }, []);

  const hasActivePlan =
    activeSubscription &&
    activeSubscription.isActive &&
    activeSubscription.endDate &&
    new Date(activeSubscription.endDate) > new Date();

  const handlePayOnce = async () => {
    // Evitar que pague de nuevo si ya tiene plan
    if (hasActivePlan) {
      navigate("/user");
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const token = await user.getIdToken();

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/paypal/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: currentPrice,
            currency: "USD",
            productId: isAnnual ? "annual_unlimited" : "monthly_unlimited",
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        console.error("❌ Error creating order:", data);
        const msg =
          data?.details?.[0]?.description ||
          data?.message ||
          "There was an error starting the PayPal checkout.";
        alert(msg);
        return;
      }

      const approveLink = data.links?.find((l) => l.rel === "approve");

      if (approveLink?.href) {
        window.location.href = approveLink.href; // Checkout de PayPal
      } else {
        console.error("❌ No approval link found in PayPal response", data);
        alert("We couldn't start the PayPal checkout. Please contact support.");
      }
    } catch (err) {
      console.error("❌ handlePayOnce error:", err);
      alert("There was an error. Please try again.");
    }
  };

  return (
    <>
      <Container className="mt-5">
        <Row style={{ justifyContent: "center" }}>
          <Col xs={12} md={12} lg={8}>
            <h1 className="text-center">Plans</h1>
            <p className="text-center">
              Start for free and unlock full access to all our illustrations
              with a one-time payment, monthly or yearly.
            </p>
          </Col>
        </Row>

        {/* Mensaje de plan activo */}
        {!loadingSub && hasActivePlan && (
          <Row className="mt-3 justify-content-center">
            <Col xs={12} md={8}>
              <Alert variant="success" className="text-center">
                You already have an active plan until{" "}
                {new Date(
                  activeSubscription.endDate
                ).toLocaleDateString()}{" "}
                🎉
                <div className="mt-3">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => navigate("/user")}
                  >
                    Go to your account
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="mt-4" style={{ justifyContent: "center" }}>
          {/* FREE PLAN */}
          <Col md={4} className="mb-4">
            <Card
              className={`h-100 ${isDarkTheme ? "dark-theme" : "light-theme"}`}
            >
              <Card.Header className="text-center">
                <h1 className="mb-0">Free</h1>
              </Card.Header>
              <Container className="mt-3 mb-2 text-center">
                <Button variant="primary" disabled>
                  Free Plan
                </Button>
              </Container>
              <Card.Body>
                <hr />
                <Table
                  className={`${isDarkTheme ? "dark-theme" : "light-theme"}`}
                  bordered
                >
                  <tbody>
                    <tr>
                      <td>✓ High Resolution PNG</td>
                    </tr>
                    <tr>
                      <td>✓ Limited Downloads</td>
                    </tr>
                    <tr>
                      <td>✓ Personal License</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          {/* PAID PLAN – MONTHLY / ANNUAL ONE-TIME PAYMENT */}
          <Col md={4} className="mb-4">
            <Card
              className={`h-100 ${isDarkTheme ? "dark-theme" : "light-theme"}`}
            >
              <Card.Header
                className="d-flex align-items-center"
                style={{ justifyContent: "space-between" }}
              >
                {/* Izquierda: precios */}
                <div className="d-flex flex-column text-start">
                  <div className="d-flex text-start align-items-center">
                    <h1 className="mb-0">
                      $
                      {isAnnual
                        ? ANNUAL_PRICE.toFixed(2)
                        : MONTHLY_PRICE.toFixed(2)}
                    </h1>
                    <h5 className="mt-3 ms-2">
                      {isAnnual ? "/year" : "/month"}
                    </h5>
                  </div>
                  <small className="text-muted">
                    {isAnnual
                      ? `≈ $${effectiveMonthlyPrice}/month, billed once`
                      : "Billed once, renew manually if you need"}
                  </small>
                </div>

                {/* Derecha: Switch Monthly / Annual */}
                <div className="custom-switch-container">
                  <div className="d-flex flex-column align-items-end">
                    <span className="mb-1 fw-semibold">{labelPlan}</span>
                    <div className="d-flex align-items-center">
                      <label className="switch-label me-2">
                        {isAnnual ? "Annual" : "Monthly"}
                      </label>
                      <label className="custom-switch">
                        <input
                          type="checkbox"
                          checked={isAnnual}
                          onChange={() => setIsAnnual(!isAnnual)}
                          disabled={hasActivePlan}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </Card.Header>

              <Container className="mt-3 mb-2 text-center">
                <Button
                  variant="primary"
                  onClick={handlePayOnce}
                  disabled={hasActivePlan}
                >
                  {hasActivePlan
                    ? "You already have an active plan"
                    : `Pay with PayPal (${labelPlan})`}
                </Button>
              </Container>

              <Card.Body>
                <hr />
                <Table
                  bordered
                  className={`${isDarkTheme ? "dark-theme" : "light-theme"}`}
                >
                  <tbody>
                    <tr>
                      <td>✓ High Resolution PNG</td>
                    </tr>
                    <tr>
                      <td>✓ Unlimited Downloads</td>
                    </tr>
                    <tr>
                      <td>✓ Access all our Resources</td>
                    </tr>
                    <tr>
                      <td>
                        ✓ Original Files (SVG, HTML, CSS, JS, Figma - others)
                      </td>
                    </tr>
                    <tr>
                      <td>✓ Early Access to new resources</td>
                    </tr>
                    <tr>
                      <td>✓ Commercial License</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* FAQ Section */}
        <Row xs={12} md={12} lg={8} className="mt-5 py-5">
          <Col>
            <h2>FAQ</h2>
            <hr />
            <Row>
              <Col>
                <h5>Which Plan Do I Need?</h5>
                <p>
                  Start with the Free plan if you just want to try Cratepic.  
                  When you're ready, choose Monthly Access for a short period
                  or Annual Access for the best value.
                </p>
              </Col>
              <Col>
                <h5>What is Full Access?</h5>
                <p>
                  Full Access gives you unlimited downloads, original source
                  files and commercial use for all our resources during your
                  access period.
                </p>
              </Col>
            </Row>
            <br />
            <hr />
            <br />
            <Row>
              <Col>
                <h5>How Can I Use Cratepic?</h5>
                <p>
                  Use the resources in your personal and professional projects
                  according to our license. For more details, read our Terms of
                  Use.
                </p>
              </Col>
              <Col>
                <h5>Can I Download Unlimited?</h5>
                <p>
                  Yes, with Full Access you can download all available
                  resources as many times as you need during your access period.
                </p>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default Pricing;
